import { Component } from "@theme/component";
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from "@theme/events";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [zoomButton] - The GAIM PDP gallery's single expand button.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    // The slideshow upgrades after this element and may still be applying `initial-slide`;
    // wait a frame so the first sync reads the slide actually on show.
    requestAnimationFrame(() => {
      if (this.isConnected) this.#syncDeferredMedia();
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {Event} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {Event} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (!(event instanceof ZoomMediaSelectedEvent)) return;

    this.slideshow?.select(this.#slideIndexOf(event.detail.index), undefined, { animate: false });
  };

  /**
   * Keeps the expand button and the autoplaying media in step with the slide on show.
   */
  #handleSlideshowSelect = () => {
    const { zoomButton } = this.refs;

    if (zoomButton) zoomButton.hidden = this.#currentZoomIndex() == null;

    this.#syncDeferredMedia();
  };

  /**
   * Mounts autoplaying deferred media (uploaded video, YouTube/Vimeo iframe) on the slide on
   * show only, and pauses it on the slides that left.
   *
   * `product-media.liquid` renders gallery video with `defer_autoplay`, so an inactive slide
   * holds nothing but its preview image until this runs. Before that, every video slide carried
   * a live iframe from the first paint, and Chrome tore down and rebuilt that iframe's compositor
   * layer on every header hide/reveal, repainting the whole page each time.
   */
  #syncDeferredMedia() {
    const { slideshow } = this;
    const slides = slideshow?.slides;

    if (!slides) return;

    const current = slideshow.current ?? 0;

    slides.forEach((slide, index) => {
      const media =
        /** @type {(HTMLElement & { loadContent(focus?: boolean): void, playMedia(): void, pauseMedia(): void }) | null} */ (
          slide.querySelector("deferred-media[autoplay]")
        );

      if (!media) return;

      const loaded = media.hasAttribute("data-media-loaded");

      if (index === current) {
        loaded ? media.playMedia() : media.loadContent(false);
      } else if (loaded) {
        media.pauseMedia();
      }
    });
  }

  /**
   * Translates a zoom dialog position into the carousel slide it came from.
   *
   * The GAIM PDP dialog omits video, so its list is shorter than the carousel's and the two
   * indices diverge after the first video. Each dialog item records its origin slide in
   * `data-slide-index`; galleries that render every media item (Horizon's) set no such attribute
   * and are already 1:1.
   *
   * @param {number} zoomIndex - The index within the zoom dialog.
   * @returns {number} The matching slide index.
   */
  #slideIndexOf(zoomIndex) {
    const slideIndex = this.refs.zoomDialogComponent?.refs.media?.[zoomIndex]?.dataset.slideIndex;

    return slideIndex == null ? zoomIndex : Number(slideIndex);
  }

  /**
   * The zoom dialog position of the slide currently on show, or null when that slide isn't in
   * the dialog (video, which is excluded — see `pdp-gallery-zoom.liquid`).
   *
   * @returns {number | null}
   */
  #currentZoomIndex() {
    const { slideshow } = this;

    if (!slideshow) return null;

    const zoomIndex = slideshow.slides?.[slideshow.current]?.dataset.zoomIndex;

    return zoomIndex == null ? null : Number(zoomIndex);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Zooms the media gallery at whichever slide is currently visible.
   *
   * Added for the GAIM PDP gallery, which has a single "expand" button rather than a
   * per-item zoom trigger. The declarative `on:click="#zoom-dialog-x/open/N"` form used by
   * the slides can't express "current index", and `open()` requires both an index and the
   * event, so the lookup has to happen here.
   *
   * No-ops on a slide the dialog doesn't contain. The button is hidden on those slides, so
   * this only catches a click racing the slide change.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  zoomCurrent(event) {
    const index = this.#currentZoomIndex();

    if (index == null) return;

    this.zoom(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
